import { useState } from "react";
import { View, EmptyState, Spinner, SettingsList } from "../base";
import { CustomVoiceCard } from "../voices/CustomVoiceCard";
import { CreateVoiceCloneDialogue } from "../voices/CreateVoiceCloneDialogue";
import { OrganizationCustomVoice } from "../../redux/reducers/organizationCustomVoicesSlice";
import { useAudioSamples } from "../../hooks/useAudioSamples";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";

export type CustomVoicesCardProps = {
  customVoices: OrganizationCustomVoice[];
  isLoading: boolean;
  onCreateVoice: (params: { name: string; description: string; language: string; file: File }) => Promise<void>;
  onUpdateVoice: (id: string, params: { name: string; description: string }) => void;
  onDeleteVoice: (id: string) => void;
  className?: string;
};

export const CustomVoicesCard = ({ customVoices, isLoading, onCreateVoice, onUpdateVoice, onDeleteVoice, className }: CustomVoicesCardProps) => {
  const [showCreateVoiceDialog, setShowCreateVoiceDialog] = useState(false);

  const audioSamples = useAudioSamples();
  const sortedVoices = [...customVoices].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

  const onClickCreateVoice = () => {
    setShowCreateVoiceDialog(true);
  };

  const onCreateDialogClose = () => {
    setShowCreateVoiceDialog(false);
  };

  return (
    <OrganizationSettingsTab
      title="Custom Voices"
      description="Create and manage custom voices for your organization"
      onClickAction={onClickCreateVoice}
      actionLabel="Create Voice"
    >
      {isLoading ? (
        <View className="flex items-center justify-center w-full h-full">
          <Spinner className="!text-brand" />
        </View>
      ) : customVoices.length === 0 ? (
        <EmptyState
          icon="mic"
          title="No custom voices yet"
          description="Create your first custom voice to get started with personalized audio experiences."
          actionLabel="Create Voice"
          onAction={onClickCreateVoice}
        />
      ) : (
        <SettingsList>
          {sortedVoices.map((voice) => (
            <CustomVoiceCard
              key={voice.id}
              voice={voice}
              isActive={audioSamples.activeSampleKey === `${voice.id}-preview`}
              onPlaySample={audioSamples.playSample}
              onUpdateVoice={onUpdateVoice}
              onDeleteVoice={onDeleteVoice}
            />
          ))}
        </SettingsList>
      )}

      <CreateVoiceCloneDialogue
        isOpen={showCreateVoiceDialog}
        onClose={onCreateDialogClose}
        onCreateVoice={onCreateVoice}
        audioSamples={audioSamples}
      />
    </OrganizationSettingsTab>
  );
};
